// src/pages/AdminSettings.js
import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";

export default function AdminSettings() {
  const { user } = useAuth();

  const [thresholdDays, setThresholdDays] = useState(30);
  const [emailPrefs, setEmailPrefs] = useState({ vehicles: true, ssg: true, lkab: true });
  const [emailIntervals, setEmailIntervals] = useState([30, 7]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  async function load() {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.get("/settings/me");
      const s = data?.settings || {};
      setThresholdDays(s.alertThresholdDays ?? 30);
      setEmailPrefs({
        vehicles: !!s.emailPrefs?.vehicles,
        ssg: !!s.emailPrefs?.ssg,
        lkab: !!s.emailPrefs?.lkab,
      });
      setEmailIntervals(Array.isArray(s.emailIntervals) ? s.emailIntervals : [30, 7]);
    } catch (e) {
      setMsg("Kunde inte läsa inställningarna.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function saveThreshold(e) {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await api.put("/settings/me", {
        alertThresholdDays: Number(thresholdDays),
        emailPrefs,
        emailIntervals,
      });
      setMsg("Sparat ✅");
    } catch {
      setMsg("Kunde inte spara.");
    } finally {
      setBusy(false);
    }
  }

  async function saveEmailSettings(e) {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await api.put("/settings/me", {
        alertThresholdDays: Number(thresholdDays),
        emailPrefs,
        emailIntervals,
      });
      setMsg("Mejl-påminnelser sparade ✅");
    } catch {
      setMsg("Kunde inte spara mejl-påminnelser.");
    } finally {
      setBusy(false);
    }
  }

  async function sendRemindersNow() {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/settings/send-now");
      setMsg(data?.mailSent ? "Utskick skickat ✅" : "Utskick triggat (mail kunde inte skickas).");
    } catch {
      setMsg("Kunde inte trigga utskick.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="container mt-5">
      <h3 className="mb-1">Admininställningar</h3>
      <p className="text-muted mb-3">Mejl skickas till: <b>{user?.email}</b></p>

      {msg && <div className="alert alert-info py-2">{msg}</div>}

      {/* Varningsgräns vid inloggning */}
      <div className="card mt-3">
        <div className="card-body">
          <h5 className="card-title">Varningsgräns vid inloggning</h5>
          <form onSubmit={saveThreshold} className="d-flex align-items-center gap-2 mt-2">
            <select
              className="form-select"
              style={{ maxWidth: 200 }}
              value={thresholdDays}
              onChange={(e) => setThresholdDays(parseInt(e.target.value, 10))}
              disabled={busy}
            >
              <option value={7}>7 dagar</option>
              <option value={30}>30 dagar</option>
              <option value={60}>60 dagar</option>
              <option value={90}>90 dagar</option>
              <option value={180}>180 dagar</option>
              <option value={365}>365 dagar</option>
            </select>

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? "Sparar…" : "Spara"}
            </button>
          </form>
        </div>
      </div>

      {/* Mejl-påminnelser */}
      <div className="card mt-3 mb-4">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Mejl-påminnelser</h5>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={sendRemindersNow}
              disabled={busy}
            >
              {busy ? "Skickar…" : "Test Mail"}
            </button>
          </div>

          <form onSubmit={saveEmailSettings} className="mt-3">
            {/* toggles */}
            <div className="mb-2">
              <div className="form-check">
                <input
                  id="prefVehicles"
                  className="form-check-input"
                  type="checkbox"
                  checked={emailPrefs.vehicles}
                  onChange={(e) => setEmailPrefs({ ...emailPrefs, vehicles: e.target.checked })}
                  disabled={busy}
                />
                <label htmlFor="prefVehicles" className="form-check-label">
                  Skicka mejl för Fordon (besiktning)
                </label>
              </div>

              <div className="form-check">
                <input
                  id="prefSSG"
                  className="form-check-input"
                  type="checkbox"
                  checked={emailPrefs.ssg}
                  onChange={(e) => setEmailPrefs({ ...emailPrefs, ssg: e.target.checked })}
                  disabled={busy}
                />
                <label htmlFor="prefSSG" className="form-check-label">
                  Skicka mejl för SSG
                </label>
              </div>

              <div className="form-check">
                <input
                  id="prefLKAB"
                  className="form-check-input"
                  type="checkbox"
                  checked={emailPrefs.lkab}
                  onChange={(e) => setEmailPrefs({ ...emailPrefs, lkab: e.target.checked })}
                  disabled={busy}
                />
                <label htmlFor="prefLKAB" className="form-check-label">
                  Skicka mejl för LKAB
                </label>
              </div>
            </div>

            {/* intervall */}
            <div className="row g-2">
              <div className="col-6">
                <label className="form-label">Intervall 1 (dagar före)</label>
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={emailIntervals[0]}
                  onChange={(e) => {
                    const v = Math.max(1, Number(e.target.value || 1));
                    setEmailIntervals([v, emailIntervals[1]]);
                  }}
                  disabled={busy}
                  required
                />
              </div>
              <div className="col-6">
                <label className="form-label">Intervall 2 (dagar före)</label>
                <input
                  type="number"
                  min={1}
                  className="form-control"
                  value={emailIntervals[1]}
                  onChange={(e) => {
                    const v = Math.max(1, Number(e.target.value || 1));
                    setEmailIntervals([emailIntervals[0], v]);
                  }}
                  disabled={busy}
                  required
                />
              </div>
            </div>

            <button type="submit" className="btn btn-primary mt-3" disabled={busy}>
              {busy ? "Sparar…" : "Spara mejl-påminnelser"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
